import React, { useState, useEffect } from "react";
import { format } from "date-fns";
import { z } from "zod";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import {
  useListApiKeys,
  getListApiKeysQueryKey,
  useCreateApiKey,
  useRevokeApiKey,
} from "@workspace/api-client-react";
import { useQueryClient } from "@tanstack/react-query";
import { useAdminAuth } from "@/contexts/admin-auth";
import AdminLayout from "@/components/admin-layout";

import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter, DialogTrigger,
} from "@/components/ui/dialog";
import {
  Form, FormControl, FormField, FormItem, FormLabel, FormMessage,
} from "@/components/ui/form";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { Plus, Trash2, Copy, AlertCircle, Check } from "lucide-react";
import { useToast } from "@/hooks/use-toast";

const createKeySchema = z.object({
  name: z.string().min(2, "Mínimo 2 caracteres").max(50, "Máximo 50 caracteres"),
});
type CreateKeyFormValues = z.infer<typeof createKeySchema>;

function ApiKeysContent() {
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const { logout } = useAdminAuth();

  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [revokeDialogOpen, setRevokeDialogOpen] = useState(false);
  const [keyToRevoke, setKeyToRevoke] = useState<{ id: string; name: string } | null>(null);
  const [newKeyData, setNewKeyData] = useState<{ name: string; key: string } | null>(null);
  const [copied, setCopied] = useState(false);

  const { data: apiKeys, isLoading, error } = useListApiKeys({
    query: { queryKey: getListApiKeysQueryKey() },
  });

  useEffect(() => {
    if (error && (error as { status?: number }).status === 401) logout();
  }, [error, logout]);

  const createKeyMutation = useCreateApiKey({
    mutation: {
      onSuccess: (data) => {
        queryClient.invalidateQueries({ queryKey: getListApiKeysQueryKey() });
        setNewKeyData({ name: (data as { name: string }).name, key: (data as { key: string }).key });
        setCreateDialogOpen(false);
        form.reset();
      },
      onError: (err) => {
        if ((err as { status?: number }).status === 401) { logout(); return; }
        toast({ title: "Erro ao criar chave", description: "Tente novamente.", variant: "destructive" });
      },
    },
  });

  const revokeKeyMutation = useRevokeApiKey({
    mutation: {
      onSuccess: () => {
        queryClient.invalidateQueries({ queryKey: getListApiKeysQueryKey() });
        setRevokeDialogOpen(false);
        setKeyToRevoke(null);
        toast({ title: "Chave revogada" });
      },
      onError: (err) => {
        if ((err as { status?: number }).status === 401) { logout(); return; }
        toast({ title: "Erro ao revogar chave", variant: "destructive" });
      },
    },
  });

  const form = useForm<CreateKeyFormValues>({
    resolver: zodResolver(createKeySchema),
    defaultValues: { name: "" },
  });

  const copyToClipboard = () => {
    if (newKeyData?.key) {
      navigator.clipboard.writeText(newKeyData.key);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  };

  return (
    <div className="p-8 max-w-5xl mx-auto space-y-8">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold tracking-tight mb-1">Chaves de API</h1>
          <p className="text-muted-foreground">Gerencie as chaves para integração com o DISC Chat.</p>
        </div>
        <Dialog open={createDialogOpen} onOpenChange={setCreateDialogOpen}>
          <DialogTrigger asChild>
            <Button data-testid="button-create-key">
              <Plus className="h-4 w-4 mr-2" />
              Nova Chave
            </Button>
          </DialogTrigger>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Criar nova chave de API</DialogTitle>
              <DialogDescription>Esta chave permitirá criar sessões de entrevista.</DialogDescription>
            </DialogHeader>
            <Form {...form}>
              <form onSubmit={form.handleSubmit((d) => createKeyMutation.mutate({ data: { name: d.name } }))} className="space-y-4 pt-4">
                <FormField control={form.control} name="name" render={({ field }) => (
                  <FormItem>
                    <FormLabel>Nome</FormLabel>
                    <FormControl>
                      <Input placeholder="ex: Integração ATS Produção" data-testid="input-key-name" {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )} />
                <DialogFooter>
                  <Button type="button" variant="outline" onClick={() => setCreateDialogOpen(false)}>Cancelar</Button>
                  <Button type="submit" disabled={createKeyMutation.isPending} data-testid="button-confirm-create">
                    {createKeyMutation.isPending ? "Criando..." : "Criar"}
                  </Button>
                </DialogFooter>
              </form>
            </Form>
          </DialogContent>
        </Dialog>
      </div>

      {newKeyData && (
        <Alert className="bg-primary/5 border-primary/20">
          <AlertCircle className="h-5 w-5 text-primary" />
          <AlertTitle className="text-primary font-semibold">Chave criada com sucesso</AlertTitle>
          <AlertDescription className="mt-2 space-y-4">
            <p>Copie e guarde essa chave agora. <strong>Ela não será exibida novamente.</strong></p>
            <div className="flex items-center gap-2">
              <code className="flex-1 bg-background border px-4 py-2.5 rounded-md text-sm font-mono break-all" data-testid="text-new-api-key">
                {newKeyData.key}
              </code>
              <Button variant="secondary" onClick={copyToClipboard} className="shrink-0 w-24" data-testid="button-copy-key">
                {copied ? <Check className="h-4 w-4 mr-2" /> : <Copy className="h-4 w-4 mr-2" />}
                {copied ? "Copiado" : "Copiar"}
              </Button>
            </div>
            <div className="flex justify-end">
              <Button variant="outline" size="sm" onClick={() => setNewKeyData(null)} data-testid="button-dismiss-key">
                Já salvei a chave
              </Button>
            </div>
          </AlertDescription>
        </Alert>
      )}

      <Card className="shadow-sm">
        <CardHeader className="pb-4">
          <CardTitle className="text-lg">Chaves ativas e revogadas</CardTitle>
        </CardHeader>
        <CardContent className="p-0">
          <Table>
            <TableHeader className="bg-muted/50">
              <TableRow>
                <TableHead className="pl-6">Nome</TableHead>
                <TableHead>Prefixo</TableHead>
                <TableHead>Status</TableHead>
                <TableHead>Criada em</TableHead>
                <TableHead>Último uso</TableHead>
                <TableHead className="text-right pr-6">Ação</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {isLoading ? Array.from({ length: 3 }).map((_, i) => (
                <TableRow key={i}>
                  {[32, 24, 16, 24, 24, 8].map((w, j) => (
                    <TableCell key={j} className={j === 0 ? "pl-6" : j === 5 ? "text-right pr-6" : ""}>
                      <Skeleton className={`h-5 w-${w}`} />
                    </TableCell>
                  ))}
                </TableRow>
              )) : apiKeys?.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={6} className="h-32 text-center text-muted-foreground" data-testid="text-no-keys">
                    Nenhuma chave encontrada. Crie uma para começar.
                  </TableCell>
                </TableRow>
              ) : apiKeys?.map((key) => (
                <TableRow key={key.id} className={!key.active ? "opacity-60 bg-muted/20" : ""} data-testid={`row-api-key-${key.id}`}>
                  <TableCell className="font-medium pl-6">{key.name}</TableCell>
                  <TableCell className="font-mono text-sm">{key.keyPrefix}••••••••</TableCell>
                  <TableCell>
                    {key.active
                      ? <Badge variant="outline" className="bg-emerald-50 text-emerald-700 border-emerald-200">Ativa</Badge>
                      : <Badge variant="outline" className="bg-destructive/10 text-destructive border-destructive/20">Revogada</Badge>}
                  </TableCell>
                  <TableCell className="text-muted-foreground text-sm">{format(new Date(key.createdAt), "dd/MM/yyyy")}</TableCell>
                  <TableCell className="text-muted-foreground text-sm">
                    {key.lastUsedAt ? format(new Date(key.lastUsedAt), "dd/MM/yyyy") : "Nunca"}
                  </TableCell>
                  <TableCell className="text-right pr-6">
                    {key.active && (
                      <Button variant="ghost" size="sm" className="text-destructive hover:text-destructive hover:bg-destructive/10"
                        onClick={() => { setKeyToRevoke({ id: key.id, name: key.name }); setRevokeDialogOpen(true); }}
                        data-testid={`button-revoke-${key.id}`}>
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    )}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      <Dialog open={revokeDialogOpen} onOpenChange={setRevokeDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Revogar chave de API</DialogTitle>
            <DialogDescription>
              Tem certeza que deseja revogar a chave <strong>"{keyToRevoke?.name}"</strong>?
              Esta ação não pode ser desfeita.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter className="mt-4">
            <Button variant="outline" onClick={() => setRevokeDialogOpen(false)}>Cancelar</Button>
            <Button variant="destructive" onClick={() => keyToRevoke && revokeKeyMutation.mutate({ keyId: keyToRevoke.id })}
              disabled={revokeKeyMutation.isPending} data-testid="button-confirm-revoke">
              {revokeKeyMutation.isPending ? "Revogando..." : "Sim, revogar"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

export default function AdminPage() {
  return (
    <AdminLayout>
      <ApiKeysContent />
    </AdminLayout>
  );
}
